<template>
  <section class="container">
    <div
      v-if="loading && !objectId"
      class="text-center mt-5">
      <coozzy-spinner />
    </div>
    <template v-else-if="linkInvalid">
      <div class="row mt-4">
        <div class="col">
          <p class="mb-0">
            {{ $t('message.marketingObjects.viewingAppointments.publicForm.invalidLink') }}
          </p>
        </div>
      </div>
    </template>
    <template v-else-if="viewMode === 'unsubscribed'">
      <div class="row">
        <div class="col">
          <coozzy-page-title
            class="mt-4"
            :wrap="true"
            :title="$tc('message.marketingObjects.viewingAppointments.selectForm.unsubscribedTitle')" />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="row">
        <div class="col">
          <coozzy-page-title
            class="mt-4"
            :wrap="true"
            :title="$tc(pageTitleKey)" />
          <p class="text-muted">
            {{ $t(pageSubtitleKey) }}
          </p>
        </div>
      </div>
      <div
        v-if="objectId"
        class="row">
        <coozzy-card class="col pl-0 pr-0 mb-3 margin15">
          <application-form-object-view :default-object-id="objectId" />
        </coozzy-card>
      </div>
      <div
        v-if="viewMode === 'confirmed' && confirmedSlot"
        class="row mb-4">
        <coozzy-card class="col pl-0 pr-0 mb-3 margin15">
          <div class="pl-3 pr-3 pt-3 pb-3">
            <div class="row align-items-center">
              <div class="col-12">
                <span class="font-weight-bold">{{ formatSlotDate(confirmedSlot) }}</span>
                <span class="ml-2">{{ formatSlotTimeRange(confirmedSlot) }}</span>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12">
                <coozzy-dropdown
                  design="default"
                  size="sm"
                  :text="$t('message.marketingObjects.viewingAppointments.selectForm.addToCalendar')">
                  <coozzy-dropdown-item
                    @click="openCalendarLink('google')">
                    {{ $t('message.marketingObjects.viewingAppointments.selectForm.calendarGoogle') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    @click="openCalendarLink('outlook')">
                    {{ $t('message.marketingObjects.viewingAppointments.selectForm.calendarOutlook') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    @click="openCalendarLink('apple')">
                    {{ $t('message.marketingObjects.viewingAppointments.selectForm.calendarApple') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    @click="openCalendarLink('ics')">
                    {{ $t('message.marketingObjects.viewingAppointments.selectForm.calendarDownloadIcs') }}
                  </coozzy-dropdown-item>
                </coozzy-dropdown>
              </div>
            </div>
            <div class="row mt-3 align-items-center">
              <div class="col-12 col-md-12 d-flex flex-wrap">
                <coozzy-button
                  class="mr-2 mb-2"
                  design="default"
                  @click="startUnsubscribe">
                  {{ $t('message.marketingObjects.viewingAppointments.selectForm.unsubscribe') }}
                </coozzy-button>
                <coozzy-button
                  class="mb-2"
                  design="default"
                  @click="changeAppointment">
                  {{ $t('message.marketingObjects.viewingAppointments.selectForm.changeAppointment') }}
                </coozzy-button>
              </div>
            </div>
            <template v-if="showUnsubscribeConfirm">
              <div class="row mt-3">
                <div class="col-12">
                  <coozzy-form-checkbox
                    :key="'unsubscribe-confirm-' + showUnsubscribeConfirm"
                    :initial="unsubscribeConfirmed"
                    clickable-label
                    @change="onUnsubscribeConfirmChange">
                    {{ $t('message.marketingObjects.viewingAppointments.selectForm.unsubscribeConfirm') }}
                  </coozzy-form-checkbox>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-12 text-right">
                  <coozzy-button
                    design="primary"
                    :disabled="!unsubscribeConfirmed || unsubscribing"
                    @click="confirmUnsubscribe">
                    {{ $t('message.marketingObjects.viewingAppointments.selectForm.send') }}
                  </coozzy-button>
                </div>
              </div>
            </template>
          </div>
        </coozzy-card>
      </div>
      <div
        v-else-if="loading"
        class="row mb-4">
        <div class="col text-center mt-3">
          <coozzy-spinner />
        </div>
      </div>
      <div
        v-else-if="!hasAvailableSlots"
        class="row mb-4">
        <div class="col">
          <p class="mb-0">
            {{ $t('message.marketingObjects.viewingAppointments.selectForm.noSlots') }}
          </p>
        </div>
      </div>
      <div
        v-else
        class="row mb-4">
        <coozzy-card
          :key="formKey"
          class="col pl-0 pr-0 pt-3 pb-3 mb-3 margin15">
          <div class="row px-3">
            <div class="col-12 col-md-3 mb-3">
              <coozzy-form-input
                v-model="form.firstName"
                :name="mandatoryFieldLabel('message.generic.firstName')"
                :state="fieldErrors.firstName === false ? false : null" />
            </div>
            <div class="col-12 col-md-3 mb-3">
              <coozzy-form-input
                v-model="form.lastName"
                :name="mandatoryFieldLabel('message.generic.lastName')"
                :state="fieldErrors.lastName === false ? false : null" />
            </div>
            <div class="col-12 col-md-3 mb-3">
              <coozzy-form-input
                v-model="form.email"
                type="email"
                :name="mandatoryFieldLabel('message.generic.email')"
                :state="fieldErrors.email === false ? false : null" />
            </div>
            <div class="col-12 col-md-3 mb-3">
              <coozzy-form-input
                v-model="form.phone"
                :name="mandatoryFieldLabel('message.generic.phoneNumber')"
                :state="fieldErrors.phone === false ? false : null" />
            </div>
          </div>
          <div
            v-for="slot in sortedAvailableSlots"
            :key="slot.id"
            class="row align-items-center mb-2 px-3">
            <div class="col-12">
              <coozzy-form-radio
                v-model="selectedAppointmentId"
                :default-model="selectedAppointmentId"
                name="viewingAppointmentSlot"
                :select-value="slot.id">
                <span class="font-weight-bold">{{ formatSlotDate(slot) }}</span>
                <span class="ml-2">{{ formatSlotTimeRange(slot) }}</span>
              </coozzy-form-radio>
            </div>
          </div>
          <div class="row mt-3 px-3">
            <div class="col-12 text-right">
              <coozzy-button
                design="primary"
                :disabled="saving || loading || !selectedAppointmentId"
                @click="submit">
                {{ $t('message.marketingObjects.viewingAppointments.selectForm.send') }}
              </coozzy-button>
            </div>
          </div>
        </coozzy-card>
      </div>
    </template>
  </section>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle.vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyDropdown from '@/framework/components/dropdown/CoozzyDropdown.vue'
import CoozzyDropdownItem from '@/framework/components/dropdown/CoozzyDropdownItem.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyFormRadio from '@/framework/components/form/radio/CoozzyFormRadio.vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import ApplicationFormObjectView from '@/marketingBasic/components/applicationForm/ApplicationFormObjectView.vue'
import CalendarSchedulerApi from '@/misc/apis/CalendarSchedulerApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import { dateUtils } from '@/mixins/dateUtils'
import { isEmailValid } from '@/mixins/validation'
import { mapGetters } from 'vuex'
import { SchedulerAppointmentSlot, LanguagePreferredLanguage } from '@/types/api'
import { compareSchedulerAppointmentSlots, formatSchedulerSlotTimeRange } from '@/misc/utils/viewingAppointmentSlotUtils'
import {
  formatViewingObjectAddress,
  openViewingAppointmentCalendar,
  type ViewingAppointmentCalendarProvider
} from '@/misc/utils/viewingAppointmentCalendar'

type SelectFormViewMode = 'select' | 'confirmed' | 'unsubscribed'

export default Vue.extend({
  name: 'SelectViewingAppointmentsLandingPage',
  components: {
    CoozzyPageTitle,
    CoozzyCard,
    CoozzyButton,
    CoozzyDropdown,
    CoozzyDropdownItem,
    CoozzySpinner,
    CoozzyFormInput,
    CoozzyFormRadio,
    CoozzyFormCheckbox,
    ApplicationFormObjectView
  },
  mixins: [dateUtils],
  metaInfo() {
    return {
      title: this.$t(this.pageTitleKey),
      meta: [
        { name: 'theme-color', content: '#eeeeee' }
      ]
    }
  },
  props: {
    token: {
      type: String as PropType<string>,
      required: true
    },
    participantId: {
      type: String as PropType<string>,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      loading: true,
      linkInvalid: false,
      saving: false,
      unsubscribing: false,
      viewMode: 'select' as SelectFormViewMode,
      objectId: '' as string,
      objectAddress: '' as string,
      availableSlots: [] as SchedulerAppointmentSlot[],
      selectedAppointmentId: '',
      confirmedSlot: null as SchedulerAppointmentSlot | null,
      registeredParticipantId: this.participantId || '',
      registrationLoaded: false,
      skipAutoSelect: false,
      formKey: 0,
      showUnsubscribeConfirm: false,
      unsubscribeConfirmed: false,
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      },
      fieldErrors: {
        firstName: null as boolean | null,
        lastName: null as boolean | null,
        email: null as boolean | null,
        phone: null as boolean | null
      }
    }
  },
  computed: {
    ...mapGetters('internationalization', ['getCurrentLanguage']),
    pageTitleKey(): string {
      if (this.viewMode === 'unsubscribed') {
        return 'message.marketingObjects.viewingAppointments.selectForm.unsubscribedTitle'
      }
      if (this.viewMode === 'confirmed') {
        return 'message.marketingObjects.viewingAppointments.selectForm.selectedTitle'
      }
      return 'message.marketingObjects.viewingAppointments.selectForm.title'
    },
    pageSubtitleKey(): string {
      if (this.viewMode === 'confirmed') {
        return 'message.marketingObjects.viewingAppointments.selectForm.selectedSubtitle'
      }
      return 'message.marketingObjects.viewingAppointments.selectForm.subtitle'
    },
    hasAvailableSlots(): boolean {
      return this.sortedAvailableSlots.length > 0
    },
    sortedAvailableSlots(): SchedulerAppointmentSlot[] {
      return [...this.availableSlots].sort(compareSchedulerAppointmentSlots)
    },
    formUrl(): string {
      return window.location.href
    },
    calendarEventTitle(): string {
      if (this.objectAddress) {
        return this.$t(
          'message.marketingObjects.viewingAppointments.selectForm.calendarEventTitle',
          { address: this.objectAddress }
        ).toString()
      }
      return this.$t('message.marketingObjects.viewingAppointments.selectForm.calendarEventTitlePlain').toString()
    },
    localeForDate(): string {
      const language = this.getCurrentLanguage || 'de'
      if (language === 'en') {
        return 'en-GB'
      }
      if (language === 'fr') {
        return 'fr-CH'
      }
      if (language === 'it') {
        return 'it-CH'
      }
      return 'de-CH'
    },
    formLanguage(): LanguagePreferredLanguage {
      switch (this.getCurrentLanguage) {
        case 'de':
          return LanguagePreferredLanguage.PREFERRED_LANGUAGE_GERMAN
        case 'en':
          return LanguagePreferredLanguage.PREFERRED_LANGUAGE_ENGLISH
        case 'it':
          return LanguagePreferredLanguage.PREFERRED_LANGUAGE_ITALIAN
        case 'fr':
          return LanguagePreferredLanguage.PREFERRED_LANGUAGE_FRENCH
        default:
          return LanguagePreferredLanguage.PREFERRED_LANGUAGE_UNDEFINED
      }
    }
  },
  watch: {
    viewMode() {
      this.$nextTick(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      })
    }
  },
  mounted() {
    const userLang = navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage || 'de'
    this.$store.dispatch('internationalization/changeCurrentLanguage', userLang.substring(0, 2))
    this.loadSlots()
  },
  beforeDestroy() {
    if (!this.$store.getters['user/getAccessToken']) {
      this.$store.dispatch('user/setLogoUrl', null)
    }
  },
  methods: {
    mandatoryFieldLabel(i18nKey: string): string {
      return this.$tc(i18nKey) + ' *'
    },
    loadSlots() {
      this.loading = true
      this.linkInvalid = false
      this.availableSlots = []
      this.selectedAppointmentId = ''
      CalendarSchedulerApi.getAvailableSlots(this.token)
        .then(response => {
          this.availableSlots = response.availableSlots || []
          this.objectId = response.objectId || ''
          if (this.objectId) {
            this.loadObjectAddress(this.objectId)
          }
          this.syncSelectedAppointmentId()
          if (this.participantId && !this.registrationLoaded) {
            this.loadRegistration()
            return
          }
          this.loading = false
        })
        .catch(e => {
          console.log(e)
          if (e?.response?.status === 404) {
            this.linkInvalid = true
          } else {
            Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
          }
          this.loading = false
        })
    },
    loadRegistration() {
      this.registrationLoaded = true
      CalendarSchedulerApi.getRegistration(this.token, this.participantId)
        .then(response => {
          if (!response?.slot || !response?.participant) {
            return
          }
          this.confirmedSlot = response.slot
          this.registeredParticipantId = response.participant.id || this.participantId
          this.selectedAppointmentId = response.slot.id || ''
          this.form.firstName = response.participant.firstName || ''
          this.form.lastName = response.participant.lastName || ''
          this.form.email = response.participant.email || ''
          this.form.phone = response.participant.phone || ''
          this.viewMode = 'confirmed'
        })
        .catch(e => {
          console.log(e)
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadObjectAddress(objectId: string) {
      ObjectApi.getMarketingObject(objectId)
        .then((response: { address?: { careOf?: string; street?: string; streetNumber?: string; zip?: string; city?: string } | null }) => {
          this.objectAddress = formatViewingObjectAddress(response?.address)
        })
        .catch((e: unknown) => {
          console.log(e)
        })
    },
    syncSelectedAppointmentId() {
      const stillAvailable = this.availableSlots.some(slot => slot.id === this.selectedAppointmentId)
      if (stillAvailable) {
        return
      }
      if (this.skipAutoSelect) {
        this.selectedAppointmentId = ''
        return
      }
      this.selectedAppointmentId = this.availableSlots.length === 1 ? this.availableSlots[0].id : ''
    },
    formatSlotDate(slot: SchedulerAppointmentSlot): string {
      if (!slot?.date) {
        return ''
      }
      const date = new Date(slot.date.year, (slot.date.month || 1) - 1, slot.date.day || 1)
      return date.toLocaleDateString(this.localeForDate, {
        weekday: 'long',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },
    formatSlotTimeRange(slot: SchedulerAppointmentSlot): string {
      return formatSchedulerSlotTimeRange(slot)
    },
    isValidPhoneFormat(phone: string): boolean {
      if (!phone || !phone.trim()) {
        return false
      }
      const digits = phone.replace(/[^0-9+]/g, '')
      const digitCount = digits.replace(/\D/g, '').length
      return digitCount >= 6 && /^\+?[0-9]+$/.test(digits)
    },
    showValidationError(messageKey: string) {
      Vue.toasted.show(this.$t(messageKey), { type: 'error', duration: 3000 })
    },
    validate(): boolean {
      this.syncSelectedAppointmentId()
      const email = this.form.email.trim()
      const phone = this.form.phone.trim()
      this.fieldErrors = {
        firstName: this.form.firstName.trim() ? null : false,
        lastName: this.form.lastName.trim() ? null : false,
        email: email ? (isEmailValid(email) ? null : false) : false,
        phone: this.isValidPhoneFormat(phone) ? null : false
      }
      if (this.fieldErrors.email === false && !email) {
        this.showValidationError('message.marketingObjects.viewingAppointments.selectForm.missingEmail')
        return false
      }
      if (this.fieldErrors.email === false) {
        this.showValidationError('message.marketingObjects.viewingAppointments.selectForm.invalidEmail')
        return false
      }
      if (this.fieldErrors.phone === false && !phone) {
        this.showValidationError('message.marketingObjects.viewingAppointments.selectForm.missingPhone')
        return false
      }
      if (this.fieldErrors.phone === false) {
        this.showValidationError('message.marketingObjects.viewingAppointments.selectForm.invalidPhone')
        return false
      }
      if (this.fieldErrors.firstName === false || this.fieldErrors.lastName === false) {
        this.showValidationError('message.marketingObjects.viewingAppointments.selectForm.missingRequiredFields')
        return false
      }
      if (!this.selectedAppointmentId) {
        this.showValidationError('message.marketingObjects.viewingAppointments.selectForm.missingSlot')
        return false
      }
      return true
    },
    submit() {
      if (this.loading || !this.validate() || this.saving) {
        return
      }
      const submittedAppointmentId = this.selectedAppointmentId
      this.saving = true
      CalendarSchedulerApi.submitSlotSelection({
        token: this.token,
        appointmentId: submittedAppointmentId,
        firstName: this.form.firstName.trim(),
        lastName: this.form.lastName.trim(),
        email: this.form.email.trim(),
        phone: this.form.phone.trim(),
        language: this.formLanguage
      })
        .then(response => {
          const participantId = response.participant?.id || ''
          if (participantId) {
            this.registeredParticipantId = participantId
            this.registrationLoaded = true
            if (this.$route.params.participantId !== participantId) {
              this.$router.replace({
                name: 'SelectViewingAppointmentsPage',
                params: {
                  token: this.token,
                  participantId
                }
              }).catch(() => undefined)
            }
          }
          Vue.toasted.show(
            this.$t('message.marketingObjects.viewingAppointments.selectForm.submittedToast'),
            { type: 'success', duration: 2500 }
          )
          this.confirmedSlot = this.availableSlots.find(slot => slot.id === submittedAppointmentId) || null
          this.viewMode = 'confirmed'
          this.showUnsubscribeConfirm = false
          this.unsubscribeConfirmed = false
          this.skipAutoSelect = false
        })
        .catch(e => {
          console.log(e)
          const status = e?.response?.status
          if (status === 404) {
            this.linkInvalid = true
          } else if (status === 400) {
            const message = (e?.response?.data?.message || '').toLowerCase()
            const emailTerms = ['email', 'e-mail', 'mail address', 'adresse email', 'adresse e-mail', 'adresse mail', 'indirizzo email', 'indirizzo e-mail']
            const phoneTerms = ['phone', 'telephone', 'telefon', 'téléphone', 'telefono', 'phone number', 'telephone number', 'telefonnummer', 'numéro de téléphone', 'numero di telefono', 'numero telefonico', 'mobile number']
            if (emailTerms.some(term => message.includes(term))) {
              this.showValidationError('message.marketingObjects.viewingAppointments.selectForm.invalidEmail')
              this.fieldErrors.email = false
            } else if (phoneTerms.some(term => message.includes(term))) {
              this.showValidationError('message.marketingObjects.viewingAppointments.selectForm.invalidPhone')
              this.fieldErrors.phone = false
            } else {
              this.showValidationError('message.savingErrors.general')
            }
          } else {
            this.showValidationError('message.savingErrors.general')
          }
        })
        .finally(() => {
          this.saving = false
        })
    },
    openCalendarLink(provider: ViewingAppointmentCalendarProvider) {
      if (!this.confirmedSlot) {
        return
      }
      openViewingAppointmentCalendar(
        {
          appointment: this.confirmedSlot,
          title: this.calendarEventTitle,
          location: this.objectAddress,
          url: this.formUrl
        },
        provider
      )
    },
    startUnsubscribe() {
      this.showUnsubscribeConfirm = true
      this.unsubscribeConfirmed = false
    },
    onUnsubscribeConfirmChange(checked: boolean) {
      this.unsubscribeConfirmed = !!checked
    },
    confirmUnsubscribe() {
      if (!this.unsubscribeConfirmed || this.unsubscribing) {
        return
      }
      if (!this.registeredParticipantId) {
        this.showValidationError('message.savingErrors.general')
        return
      }
      this.unsubscribing = true
      CalendarSchedulerApi.cancelRegistration(this.token, this.registeredParticipantId)
        .then(() => {
          if (typeof Vue.toasted.clear === 'function') {
            Vue.toasted.clear()
          }
          this.viewMode = 'unsubscribed'
          this.confirmedSlot = null
          this.registeredParticipantId = ''
          this.showUnsubscribeConfirm = false
          this.unsubscribeConfirmed = false
        })
        .catch(e => {
          console.log(e)
          if (e?.response?.status === 404) {
            this.linkInvalid = true
          } else {
            this.showValidationError('message.savingErrors.general')
          }
        })
        .finally(() => {
          this.unsubscribing = false
        })
    },
    changeAppointment() {
      if (typeof Vue.toasted.clear === 'function') {
        Vue.toasted.clear()
      }
      this.viewMode = 'select'
      this.confirmedSlot = null
      this.showUnsubscribeConfirm = false
      this.unsubscribeConfirmed = false
      this.skipAutoSelect = true
      this.selectedAppointmentId = ''
      this.formKey += 1
      this.loadSlots()
    }
  }
})
</script>

<style lang="scss" scoped>
.margin15 {
  margin: 0 15px;
}
</style>

<style lang="scss">
/* On mobile vue-toasted stretches full-width as a green success bar.
   Keep it below the public header so it does not cover the page title. */
@media (max-width: 600px) {
  .toasted-container.top-center {
    top: 72px !important;
  }
}
</style>
